import { Type } from '@scalar/typebox';
import { compose } from '../../compose.js';
import { XInternalSchema } from '../../extensions/document/x-internal.js';
import { XScalarIgnoreSchema } from '../../extensions/document/x-scalar-ignore.js';
import { XTagsSchema } from '../../extensions/document/x-tags.js';
import { XAdditionalPropertiesNameSchema, } from '../../extensions/schema/x-additional-properties-name.js';
import { XEnumDescriptionsSchema } from '../../extensions/schema/x-enum-descriptions.js';
import { XEnumVarNamesSchema } from '../../extensions/schema/x-enum-varnames.js';
import { XExamplesSchema } from '../../extensions/schema/x-examples.js';
import { XOrderSchema } from '../../extensions/schema/x-order.js';
import { XVariableSchema } from '../../extensions/schema/x-variable.js';
import { DiscriminatorObjectRef, ExternalDocumentationObjectRef, SchemaObjectRef, XMLObjectRef, } from './ref-definitions.js';
import { ReferenceObjectSchema } from './reference.js';
/**
 * A schema position can hold either a schema object or a reference to one.
 *
 * The reference variant keeps `$ref-value` optional on purpose: an unresolved
 * reference (for example a sparse chunk `$ref` produced by the server store) is
 * just `{ $ref }` with no resolved value yet. If the reference variant required
 * `$ref-value`, such a value would match neither variant, so coercion would fall
 * back to the schema-object variant and silently drop the `$ref`, which breaks
 * lazy chunk resolution. With `$ref-value` optional the `{ $ref }` already matches
 * the reference variant and is preserved unchanged, independent of order.
 *
 * The schema-object variant comes first so that a value matching neither variant
 * (genuinely invalid input, never a reference) falls back to an empty schema
 * object rather than a bogus `{ $ref: '' }` that would read as a real reference.
 */
const schemaOrReference = Type.Union([
    SchemaObjectRef,
    compose(ReferenceObjectSchema, Type.Object({ '$ref-value': Type.Optional(Type.Unknown()) })),
]);
const PrimitiveSchemaTypeSchema = Type.Union([
    Type.Literal('null'),
    Type.Literal('boolean'),
    Type.Literal('string'),
    Type.Literal('number'),
    Type.Literal('integer'),
    Type.Literal('object'),
    Type.Literal('array'),
]);
/**
 * Primitive types that don't have additional validation properties.
 * These types (null, boolean) can be used
 * without additional validation constraints.
 */
const OtherTypes = Type.Object({
    type: Type.Union([Type.Literal('null'), Type.Literal('boolean')]),
});
const Extensions = compose(XScalarIgnoreSchema, XInternalSchema, XVariableSchema, XExamplesSchema, XEnumDescriptionsSchema, XEnumVarNamesSchema, XAdditionalPropertiesNameSchema, XOrderSchema, XTagsSchema);
const CorePropertiesWithSchema = Type.Object({
    /**
     * JSON Schema 2020-12 core reference keywords.
     *
     * OpenAPI 3.1 adopts the JSON Schema 2020-12 dialect for Schema Objects, which means a schema may carry an
     * identifier (`$id`), a plain-name anchor (`$anchor`), and the dynamic binding keywords (`$dynamicAnchor` /
     * `$dynamicRef`) used for generic and recursive patterns such as `PaginatedResponse<T>`. We keep these typed so
     * the keywords survive parsing instead of being dropped as unknown properties. Resolution of `$dynamicRef`
     * against the active `$dynamicAnchor` is tracked separately, see https://github.com/scalar/scalar/issues/9414.
     */
    $id: Type.Optional(Type.String()),
    /** Plain-name anchor that other schemas can reference with `#anchor`. */
    $anchor: Type.Optional(Type.String()),
    /** Dynamic anchor, the target a matching `$dynamicRef` resolves to within the dynamic scope. */
    $dynamicAnchor: Type.Optional(Type.String()),
    /** Dynamic reference, resolved against the outermost matching `$dynamicAnchor` in the dynamic scope. */
    $dynamicRef: Type.Optional(Type.String()),
    name: Type.Optional(Type.String()),
    /** A title for the schema. */
    title: Type.Optional(Type.String()),
    /** A description of the schema. */
    description: Type.Optional(Type.String()),
    /** Default value for the schema. */
    default: Type.Optional(Type.Unknown()),
    /** Array of allowed values. */
    enum: Type.Optional(Type.Array(Type.Unknown())),
    /** Constant value that must match exactly. */
    const: Type.Optional(Type.Unknown()),
    /** Media type for content validation. */
    contentMediaType: Type.Optional(Type.String()),
    /** Content encoding. */
    contentEncoding: Type.Optional(Type.String()),
    /** Schema for content validation. */
    contentSchema: Type.Optional(schemaOrReference),
    /** Whether the schema is deprecated. */
    deprecated: Type.Optional(Type.Boolean()),
    /** Adds support for polymorphism. The discriminator is used to determine which of a set of schemas a payload is expected to satisfy. See Composition and Inheritance for more details. */
    discriminator: Type.Optional(DiscriminatorObjectRef),
    /** Whether the schema is read-only. */
    readOnly: Type.Optional(Type.Boolean()),
    /** Whether the schema is write-only. */
    writeOnly: Type.Optional(Type.Boolean()),
    /** This MAY be used only on property schemas. It has no effect on root schemas. Adds additional metadata to describe the XML representation of this property. */
    xml: Type.Optional(XMLObjectRef),
    /** Additional external documentation for this schema. */
    externalDocs: Type.Optional(ExternalDocumentationObjectRef),
    /**
     * A free-form field to include an example of an instance for this schema. To represent examples that cannot be naturally represented in JSON or YAML, a string value can be used to contain the example with escaping where necessary.
     *
     * @deprecated The example field has been deprecated in favor of the JSON Schema examples keyword. Use of example is discouraged, and later versions of this specification may remove it.
     */
    example: Type.Optional(Type.Unknown()),
    /**
     * An array of examples of valid instances for this schema. This keyword follows the JSON Schema Draft 2020-12 specification.
     * Each example should be a valid instance of the schema.
     */
    examples: Type.Optional(Type.Array(Type.Unknown())),
    /** All schemas must be valid. */
    allOf: Type.Optional(Type.Array(schemaOrReference)),
    /** Exactly one schema must be valid. */
    oneOf: Type.Optional(Type.Array(schemaOrReference)),
    /** At least one schema must be valid. */
    anyOf: Type.Optional(Type.Array(schemaOrReference)),
    /** Schema must not be valid. */
    not: Type.Optional(schemaOrReference),
});
/**
 * Numeric validation properties for number and integer types.
 */
const NumericValidationKeywords = Type.Object({
    /** Number must be a multiple of this value. */
    multipleOf: Type.Optional(Type.Number()),
    /** Maximum value (inclusive). */
    maximum: Type.Optional(Type.Number()),
    /** Maximum value (exclusive). */
    exclusiveMaximum: Type.Optional(Type.Number({ minimum: 0 })),
    /** Minimum value (inclusive). */
    minimum: Type.Optional(Type.Number()),
    /** Minimum value (exclusive). */
    exclusiveMinimum: Type.Optional(Type.Number({ minimum: 0 })),
});
const NumericProperties = compose(Type.Object({
    type: Type.Union([Type.Literal('number'), Type.Literal('integer')]),
    /** Different subtypes */
    format: Type.Optional(Type.String()),
}), NumericValidationKeywords);
/**
 * String validation properties for string types.
 */
const StringValidationKeywords = Type.Object({
    /** Maximum string length. */
    maxLength: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Minimum string length. */
    minLength: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Regular expression pattern. */
    pattern: Type.Optional(Type.String()),
});
const StringValidationProperties = compose(Type.Object({
    type: Type.Literal('string'),
    /** Different subtypes - allow any arbitrary string, this negates the purpose of having a union of formats so we type it in typescript instead */
    format: Type.Optional(Type.String()),
}), StringValidationKeywords);
const ArrayValidationKeywordsWithSchema = Type.Object({
    /** Maximum number of items in array. */
    maxItems: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Minimum number of items in array. */
    minItems: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Whether array items must be unique. */
    uniqueItems: Type.Optional(Type.Boolean()),
    /** Schema for array items. */
    items: Type.Optional(schemaOrReference),
    /** Schema for tuple validation. */
    prefixItems: Type.Optional(Type.Array(schemaOrReference)),
});
const ArrayValidationPropertiesWithSchema = compose(Type.Object({
    type: Type.Literal('array'),
}), ArrayValidationKeywordsWithSchema);
const ObjectValidationKeywordsWithSchema = Type.Object({
    /** Maximum number of properties. */
    maxProperties: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Minimum number of properties. */
    minProperties: Type.Optional(Type.Integer({ minimum: 0 })),
    /** Array of required property names. */
    required: Type.Optional(Type.Array(Type.String())),
    /** Object property definitions. */
    properties: Type.Optional(Type.Record(Type.String(), schemaOrReference)),
    /** Schema for additional properties. */
    additionalProperties: Type.Optional(Type.Union([Type.Boolean(), schemaOrReference])),
    /** Properties matching regex patterns. */
    patternProperties: Type.Optional(Type.Record(Type.String(), schemaOrReference)),
    /** Constraints on property names (JSON Schema propertyNames keyword). */
    propertyNames: Type.Optional(schemaOrReference),
});
const ObjectValidationPropertiesWithSchema = compose(Type.Object({
    type: Type.Literal('object'),
}), ObjectValidationKeywordsWithSchema);
const MultiTypeValidationPropertiesWithSchema = compose(Type.Object({
    type: Type.Array(PrimitiveSchemaTypeSchema),
    /** Different subtypes - allow any arbitrary string, this negates the purpose of having a union of formats so we type it in typescript instead */
    format: Type.Optional(Type.String()),
}), NumericValidationKeywords, StringValidationKeywords, ArrayValidationKeywordsWithSchema, ObjectValidationKeywordsWithSchema);
/** Builds the recursive schema schema */
export const SchemaObjectSchemaDefinition = Type.Union([
    // Keep compositions first so they get priority when union is evaluated
    // Make sure there is always a required field so not all properties are optional
    // When all properties are optional (1) typescript will not throw any warnings/error and accepts anything
    // even a non resolved ref and (2) it will match any schema so it will not validate the refs correctly
    compose(Type.Object({ __scalar_: Type.String() }), CorePropertiesWithSchema, Extensions),
    compose(OtherTypes, CorePropertiesWithSchema, Extensions),
    compose(NumericProperties, CorePropertiesWithSchema, Extensions),
    compose(StringValidationProperties, CorePropertiesWithSchema, Extensions),
    compose(ObjectValidationPropertiesWithSchema, CorePropertiesWithSchema, Extensions),
    compose(ArrayValidationPropertiesWithSchema, CorePropertiesWithSchema, Extensions),
    compose(MultiTypeValidationPropertiesWithSchema, CorePropertiesWithSchema, Extensions),
]);
